import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Type, Contrast, Link2, Pause, Keyboard, RotateCcw, FileText, Accessibility as AccessIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const STORAGE_KEY = "truth_or_debt_a11y";

const DEFAULTS = {
  noAnimations: false,
  highContrast: false,
  readableFont: false,
  highlightLinks: false,
  keyboardFocus: false,
  fontScale: 1, // 1.0 = 100%, max 2.0 = 200%
};

const CLASS_MAP = {
  noAnimations: "a11y-no-animations",
  highContrast: "a11y-high-contrast",
  readableFont: "a11y-readable-font",
  highlightLinks: "a11y-highlight-links",
  keyboardFocus: "a11y-keyboard-focus",
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch (err) {
    console.warn("A11y: read failed", err?.message);
    return { ...DEFAULTS };
  }
}

function applyState(state) {
  if (typeof document === "undefined") return;
  const body = document.body;
  Object.entries(CLASS_MAP).forEach(([key, cls]) => {
    body.classList.toggle(cls, !!state[key]);
  });
  // Font scale via root font-size (1rem-based Tailwind scales everything)
  document.documentElement.style.setProperty("--a11y-font-scale", String(state.fontScale || 1));
  document.documentElement.style.fontSize = `${16 * (state.fontScale || 1)}px`;
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const [state, setState] = useState(DEFAULTS);

  // Initial load + apply
  useEffect(() => {
    const s = loadState();
    setState(s);
    applyState(s);
  }, []);

  // Persist + apply on every change
  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (err) {
        console.warn("A11y: persist failed", err?.message);
      }
      applyState(next);
      return next;
    });
  }, []);

  const toggle = (key) => update({ [key]: !state[key] });

  const incFont = () => update({ fontScale: Math.min(2.0, Math.round((state.fontScale + 0.1) * 10) / 10) });
  const decFont = () => update({ fontScale: Math.max(0.8, Math.round((state.fontScale - 0.1) * 10) / 10) });

  const reset = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.warn("A11y: clear failed", err?.message);
    }
    setState(DEFAULTS);
    applyState(DEFAULTS);
  };

  // Expose globals so footers/other components can open from anywhere
  useEffect(() => {
    window.openAccessibilityPanel = () => setOpen(true);
    window.openAccessibilityStatement = () => setStatementOpen(true);
    return () => {
      delete window.openAccessibilityPanel;
      delete window.openAccessibilityStatement;
    };
  }, []);

  return (
    <>
      {/* Floating FAB */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        onClick={() => setOpen(true)}
        aria-label="פתח תפריט נגישות"
        data-testid="a11y-fab"
        className="fixed bottom-5 left-5 z-[55] w-14 h-14 rounded-full bg-[#1c1917] hover:bg-[#292524] flex items-center justify-center shadow-[0_8px_30px_rgba(0,0,0,0.35)] focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400"
      >
        <AccessIcon className="w-7 h-7 text-brand-amber" strokeWidth={2.5} />
      </motion.button>

      {/* Side panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/40 z-[70]"
              data-testid="a11y-overlay"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="fixed top-0 left-0 bottom-0 w-full max-w-sm bg-white z-[71] overflow-y-auto"
              data-testid="a11y-panel"
              dir="rtl"
              role="dialog"
              aria-label="תפריט נגישות"
            >
              <div className="sticky top-0 bg-white border-b border-stone-200 p-5 flex items-center justify-between">
                <h2 className="text-2xl font-[800]">התאמות נגישות</h2>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="סגור תפריט נגישות"
                  data-testid="a11y-close"
                  className="w-11 h-11 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-5">
                {/* Font size group */}
                <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Type className="w-5 h-5 text-brand-amber" />
                    <h3 className="font-[800]">גודל טקסט</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={decFont}
                      aria-label="הקטן טקסט"
                      data-testid="a11y-font-dec"
                      disabled={state.fontScale <= 0.8}
                      className="flex-1 h-12 rounded-xl bg-white border-2 border-stone-200 hover:border-brand-amber disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <div className="px-4 font-bold text-lg min-w-[70px] text-center" data-testid="a11y-font-scale">
                      {Math.round(state.fontScale * 100)}%
                    </div>
                    <button
                      onClick={incFont}
                      aria-label="הגדל טקסט"
                      data-testid="a11y-font-inc"
                      disabled={state.fontScale >= 2.0}
                      className="flex-1 h-12 rounded-xl bg-white border-2 border-stone-200 hover:border-brand-amber disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Toggle buttons */}
                <ToggleButton
                  testId="a11y-toggle-contrast"
                  icon={Contrast}
                  label="ניגודיות עזה"
                  description="רקע שחור, טקסט צהוב, ביטול תמונות רקע"
                  active={state.highContrast}
                  onClick={() => toggle("highContrast")}
                />
                <ToggleButton
                  testId="a11y-toggle-readable"
                  icon={Type}
                  label="גופן קריא"
                  description="החלפה לפונט Arial סטנדרטי לקריאה קלה יותר"
                  active={state.readableFont}
                  onClick={() => toggle("readableFont")}
                />
                <ToggleButton
                  testId="a11y-toggle-links"
                  icon={Link2}
                  label="הדגשת קישורים"
                  description="קו תחתון עבה ורקע צהוב לכל הקישורים"
                  active={state.highlightLinks}
                  onClick={() => toggle("highlightLinks")}
                />
                <ToggleButton
                  testId="a11y-toggle-animations"
                  icon={Pause}
                  label="עצירת אנימציות"
                  description="ביטול כל האנימציות והמעברים באתר"
                  active={state.noAnimations}
                  onClick={() => toggle("noAnimations")}
                />
                <ToggleButton
                  testId="a11y-toggle-keyboard"
                  icon={Keyboard}
                  label="ניווט מקלדת"
                  description="מסגרת פוקוס בולטת בכל אלמנט בעת ניווט TAB"
                  active={state.keyboardFocus}
                  onClick={() => toggle("keyboardFocus")}
                />

                {/* Footer action */}
                <div className="border-t border-stone-200 pt-4">
                  <Button
                    onClick={reset}
                    variant="outline"
                    data-testid="a11y-reset"
                    className="w-full h-12 rounded-xl border-2 font-bold"
                  >
                    <RotateCcw className="w-4 h-4 me-2" />
                    איפוס הגדרות
                  </Button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Accessibility Statement Dialog */}
      <Dialog open={statementOpen} onOpenChange={setStatementOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl" data-testid="a11y-statement-dialog">
          <DialogHeader>
            <DialogTitle className="font-[800] text-2xl">הצהרת נגישות</DialogTitle>
            <DialogDescription className="text-stone-500">
              אתר "אמת או חובות" — מחויבות לנגישות לכל
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-stone-700 leading-[1.8] mt-2">
            <p>
              אנו ב"אמת או חובות" רואים חשיבות עליונה בהנגשת האתר לאנשים עם מוגבלויות,
              ופועלים בהתאם להוראות חוק שוויון זכויות לאנשים עם מוגבלות, תשנ"ח-1998
              ולתקנותיו, ובהתאם לתקן הישראלי לנגישות (ת"י 5568) ברמת AA.
            </p>

            <h3 className="text-lg font-[800] mt-4">ההתאמות שביצענו</h3>
            <ul className="list-disc ps-5 space-y-1.5 marker:text-brand-amber">
              <li>תמיכה בטכנולוגיות מסייעות כגון קוראי מסך (NVDA, JAWS).</li>
              <li>ניווט מלא באמצעות מקלדת (TAB / Enter / Shift+TAB).</li>
              <li>אפשרות להגדלה והקטנה של גודל הטקסט עד 200%.</li>
              <li>מצב ניגודיות גבוהה (רקע שחור / טקסט צהוב).</li>
              <li>החלפה לגופן קריא (Arial/Tahoma) למשתמשים עם דיסלקציה.</li>
              <li>אפשרות לעצור אנימציות (חשוב לסובלים מאפילפסיה והפרעות קשב).</li>
              <li>תיוג סמנטי מלא של אלמנטים (aria-label, aria-hidden).</li>
              <li>קישור "דלג לתוכן המרכזי" בתחילת כל דף.</li>
              <li>טקסט חלופי (alt) לכל התמונות.</li>
            </ul>

            <h3 className="text-lg font-[800] mt-4">סייג טכנולוגי</h3>
            <p>
              למרות מאמצינו, ייתכן ויתגלו דפים או חלקים שטרם הונגשו במלואם.
              אנו ממשיכים לעבוד כל הזמן על שיפור הנגישות באתר, וכל פנייה בנושא
              תזכה לטיפול מהיר.
            </p>

            <h3 className="text-lg font-[800] mt-4">דרכי פנייה לרכז הנגישות</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
              <p><strong>אימייל:</strong> <a href="mailto:accessibility@truth-or-debt.co.il" className="ltr text-brand-amber underline">accessibility@truth-or-debt.co.il</a></p>
              <p><strong>שעות מענה:</strong> ימים א'-ה', בין השעות 9:00-17:00.</p>
              <p>אנו מתחייבים לחזור לכל פנייה תוך 5 ימי עסקים.</p>
            </div>

            <p className="text-xs text-stone-500 mt-6">
              הצהרה זו עודכנה לאחרונה: יוני 2026.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ToggleButton({ icon: Icon, label, description, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`w-full text-right p-4 rounded-2xl border-2 transition-colors flex items-start gap-3 focus:outline-none focus-visible:ring-4 focus-visible:ring-yellow-400 ${
        active
          ? "border-brand-amber bg-amber-50"
          : "border-stone-200 bg-white hover:border-stone-300"
      }`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? "bg-brand-amber text-white" : "bg-stone-100 text-stone-600"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold mb-0.5 flex items-center gap-2">
          {label}
          {active && <span className="text-xs font-bold text-emerald-600">● פעיל</span>}
        </div>
        <div className="text-sm text-stone-600 leading-snug">{description}</div>
      </div>
    </button>
  );
}
